// src/subscreens/Auth/AuthPage.js
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaGraduationCap,
  FaEye,
  FaEyeSlash,
  FaEnvelope,
  FaLock,
  FaUser,
} from "react-icons/fa";
import "../../assets/css/login/Login.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../context/AppContext";
import { Spinner } from "react-bootstrap";

const Login = () => {
  const { storeLoggedInStudent, baseUrl, regExpChecker } = useContext(AppContext);

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fullname, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  // validation errors
  const [fullnameError, setFullnameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const navigate = useNavigate();

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setFullnameError("");
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const switchTab = (loginMode) => {
    setIsLogin(loginMode);
    resetForm();
  };

  const validateFullname = (val) => {
    if (!val || val.trim().length < 3) return "Full name must be at least 3 characters.";
    return "";
  };

  const validateConfirmPassword = (val) => {
    if (val !== password) return "Passwords do not match.";
    return "";
  };

  // ── LOGIN ──────────────────────────────────────
  const studentLogin = () => {
    const emailErr = regExpChecker("email", email);
    const passErr = regExpChecker("password", password);

    if (emailErr || passErr) {
      setEmailError(emailErr);
      setPasswordError(passErr);
      return;
    }

    setLoginLoading(true);
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/student/auth/login`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setLoginLoading(false);
        if (response.data.success) {
          storeLoggedInStudent(response.data.student);
          localStorage.setItem("loggedInStudent", JSON.stringify(response.data.student));
          navigate("/student-dashboard");
        } else {
          Swal.fire("Oops!", "Your credentials are not correct.", "error");
        }
      })
      .catch((error) => {
        setLoginLoading(false);
        console.log(error);
      });
  };

  // ── REGISTER ───────────────────────────────────
  const studentRegister = () => {
    const fnErr = validateFullname(fullname);
    const emailErr = regExpChecker("email", email);
    const passErr = regExpChecker("password", password);
    const confErr = validateConfirmPassword(confirmPassword);

    setFullnameError(fnErr);
    setEmailError(emailErr);
    setPasswordError(passErr);
    setConfirmPasswordError(confErr);

    if (fnErr || emailErr || passErr || confErr) return;

    setRegisterLoading(true);
    const formData = new FormData();
    formData.append("fullname", fullname);
    formData.append("email", email);
    formData.append("password", password);

    axios
      .post(`${baseUrl}/student/auth/signup`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        setRegisterLoading(false);
        if (response.data.success) {
          Swal.fire("Success!", "Account created successfully. Please login.", "success");
          switchTab(true);
        } else {
          Swal.fire("Oops!", response.data.message || "Registration failed.", "error");
        }
      })
      .catch((error) => {
        setRegisterLoading(false);
        console.log(error);
        Swal.fire("Error!", "Something went wrong. Please try again.", "error");
      });
  };

  return (
    <div className="ap-auth-page">
      <div className="ap-auth-background"></div>

      <div className="container">
        <div className="row justify-content-center align-items-center min-vh-100">
          <div className="col-lg-10 col-xl-9">
            <div className="ap-auth-card">
              <div className="row g-0">

                {/* LEFT BRANDING */}
                <div className="col-lg-5 d-none d-lg-block">
                  <div className="ap-auth-branding">
                    <div className="ap-branding-content">
                      <div className="ap-brand-logo">
                        <FaGraduationCap size={60} />
                        <h2>CircleHub</h2>
                      </div>
                      <h3 className="ap-tagline">Learn Better, Together</h3>
                      <p className="ap-brand-description">
                        AI-powered learning &amp; collaborative study platform.
                      </p>
                      {!isLogin && (
                        <div className="ap-features">
                          <div className="ap-feature-item">✦ Smart AI Tutoring</div>
                          <div className="ap-feature-item">✦ Collaborative Groups</div>
                          <div className="ap-feature-item">✦ Progress Tracking</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT FORM */}
                <div className="col-lg-7">
                  <div className="ap-auth-form-container">

                    {/* TAB TOGGLE */}
                    <div className="ap-auth-toggle">
                      <button
                        className={`ap-toggle-btn ${isLogin ? "active" : ""}`}
                        onClick={() => switchTab(true)}
                      >
                        Login
                      </button>
                      <button
                        className={`ap-toggle-btn ${!isLogin ? "active" : ""}`}
                        onClick={() => switchTab(false)}
                      >
                        Register
                      </button>
                    </div>

                    <form className="ap-auth-form">
                      <h3 className="ap-form-title">
                        {isLogin ? "Welcome Back" : "Create Account"}
                      </h3>
                      {!isLogin && (
                        <p className="ap-form-subtitle">
                          Join thousands of students learning smarter
                        </p>
                      )}

                      {/* FULL NAME – register only */}
                      {!isLogin && (
                        <div className="ap-form-group">
                          <label>Full Name</label>
                          <div className="ap-input-wrapper">
                            <FaUser className="ap-input-icon" />
                            <input
                              type="text"
                              className="ap-form-control"
                              placeholder="John Doe"
                              value={fullname}
                              onChange={(e) => {
                                setFullName(e.target.value);
                                setFullnameError(validateFullname(e.target.value));
                              }}
                            />
                          </div>
                          {fullnameError && (
                            <div className="ap-error-msg">{fullnameError}</div>
                          )}
                        </div>
                      )}

                      {/* EMAIL */}
                      <div className="ap-form-group">
                        <label>Email</label>
                        <div className="ap-input-wrapper">
                          <FaEnvelope className="ap-input-icon" />
                          <input
                            type="text"
                            className="ap-form-control"
                            placeholder="student@gmail.com"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailError(regExpChecker("email", e.target.value));
                            }}
                          />
                        </div>
                        {emailError && (
                          <div className="ap-error-msg">{emailError}</div>
                        )}
                      </div>

                      {/* PASSWORD */}
                      <div className="ap-form-group">
                        <label>Password</label>
                        <div className="ap-input-wrapper">
                          <FaLock className="ap-input-icon" />
                          <input
                            type={showPassword ? "text" : "password"}
                            className="ap-form-control"
                            placeholder="********"
                            value={password}
                            onChange={(e) => {
                              setPassword(e.target.value);
                              setPasswordError(regExpChecker("password", e.target.value));
                            }}
                          />
                          <button
                            type="button"
                            className="ap-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        {passwordError && (
                          <div className="ap-error-msg">{passwordError}</div>
                        )}
                      </div>

                      {/* CONFIRM PASSWORD – register only */}
                      {!isLogin && (
                        <div className="ap-form-group">
                          <label>Confirm Password</label>
                          <div className="ap-input-wrapper">
                            <FaLock className="ap-input-icon" />
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className="ap-form-control"
                              placeholder="********"
                              value={confirmPassword}
                              onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setConfirmPasswordError(validateConfirmPassword(e.target.value));
                              }}
                            />
                            <button
                              type="button"
                              className="ap-password-toggle"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          {confirmPasswordError && (
                            <div className="ap-error-msg">{confirmPasswordError}</div>
                          )}
                        </div>
                      )}

                      {/* SUBMIT BUTTON */}
                      {(isLogin ? loginLoading : registerLoading) ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ padding: "16px 0" }}>
                          <Spinner size={"lg"} />
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="ap-btn-auth"
                          onClick={isLogin ? studentLogin : studentRegister}
                        >
                          {isLogin ? "Login" : "Create Account"}
                        </button>
                      )}

                      {/* FOOTER LINK */}
                      <div className="ap-form-footer">
                        {isLogin ? (
                          <>
                            Don't have an account?{" "}
                            <button
                              type="button"
                              className="ap-link-btn"
                              onClick={() => switchTab(false)}
                            >
                              Register
                            </button>
                          </>
                        ) : (
                          <>
                            Already have an account?{" "}
                            <button
                              type="button"
                              className="ap-link-btn"
                              onClick={() => switchTab(true)}
                            >
                              Login
                            </button>
                          </>
                        )}
                      </div>

                    </form>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;